/**
 * Deterministic guards and fixes for the generated title and description.
 * Browser-safe, pure.
 *
 * Ported from the 2026-10-17 test runs. Hard issues go to the one repair
 * pass; whatever is still flagged afterwards becomes a review item. The
 * guards cover YouTube's limits and chapter rules, banned words, em dashes
 * and placeholders, repetition (4-word windows), wording drift (interior
 * positions only), meta-language about "the client", credentials that must
 * keep "former", sentence-case titles, people named who are not confirmed,
 * and titles that may never be truncated (an over-long title is dropped).
 */

import {
	YOUTUBE_CHAPTER_MIN_COUNT,
	YOUTUBE_CHAPTER_MIN_SEC,
	YOUTUBE_DESCRIPTION_FOLD_CHARS,
	YOUTUBE_DESCRIPTION_MAX_BYTES,
	YOUTUBE_TITLE_MAX_CHARS,
} from "./limits";
import { parseSegmentRef, utf8ByteLength } from "./render-description";
import type {
	CheckIssue,
	DescriptionChapter,
	GeneratedOutput,
	TranscriptSegment,
	VideoPerson,
	VideoType,
} from "./types";

// ---------------------------------------------------------------------------
// Style vocabulary (the substance of revise-content.ts's guardrails)
// ---------------------------------------------------------------------------

export const VIDEO_AI_TELL_PHRASES = [
	"in today's fast-paced world",
	"in the ever-evolving landscape",
	"it's worth noting",
	"it's important to note",
	"dive into",
	"deep dive",
	"unlock",
	"unleash",
	"elevate",
	"navigate the complexities",
	"in conclusion",
	"at the end of the day",
	"game-changer",
	"seamless",
	"robust",
	"leverage (as a verb for 'use')",
	"whether you're … or …",
	"look no further",
	"the world of",
	"when it comes to",
] as const;

export const VIDEO_BANNED_TERMS = [
	"honest",
	"honestly",
	"genuine",
	"genuinely",
	"in plain English",
	"simply put",
] as const;

export const VIDEO_HYPE_WORDS = [
	"bombshell",
	"shocking",
	"stunning",
	"explosive",
	"jaw-dropping",
	"must-watch",
	"incredible",
	"unbelievable",
	"game-changing",
	"groundbreaking",
	"revolutionary",
	"ultimate",
] as const;

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const wordRe = (w: string) =>
	new RegExp(`(^|[^A-Za-z])${escapeRegExp(w)}([^A-Za-z]|$)`, "i");
const AI_TELL_CHECKS = VIDEO_AI_TELL_PHRASES.map((p) =>
	p.replace(/ \(.*\)$/, ""),
).filter((p) => !p.includes("…"));

/** Style issues in one piece of published text. */
export function styleIssues(label: string, text: string): CheckIssue[] {
	const issues: CheckIssue[] = [];
	if (/—/.test(text)) {
		issues.push({
			kind: "em-dash",
			fixable: true,
			msg: `${label} contains an em dash.`,
		});
	}
	if (/[<>]/.test(text)) {
		issues.push({
			kind: "angle-bracket",
			fixable: true,
			msg: `${label} contains < or >.`,
		});
	}
	for (const w of VIDEO_BANNED_TERMS) {
		if (wordRe(w).test(text)) {
			issues.push({
				kind: "banned-word",
				msg: `${label} uses the banned phrase "${w}".`,
			});
		}
	}
	if (/\bcarr(y|ies|ied|ying)\b/i.test(text)) {
		issues.push({
			kind: "carry",
			msg: `${label} uses "carry" (banned when figurative).`,
		});
	}
	for (const w of VIDEO_HYPE_WORDS) {
		if (wordRe(w).test(text)) {
			issues.push({
				kind: "hype",
				msg: `${label} uses the hype word "${w}".`,
			});
		}
	}
	const lower = text.toLowerCase();
	for (const p of AI_TELL_CHECKS) {
		if (lower.includes(p.toLowerCase())) {
			issues.push({
				kind: "ai-tell",
				msg: `${label} uses the AI-tell phrase "${p}".`,
			});
		}
	}
	if (/#[A-Za-z]/.test(text)) {
		issues.push({ kind: "hashtag", msg: `${label} contains a hashtag.` });
	}
	if (/\p{Extended_Pictographic}/u.test(text)) {
		issues.push({ kind: "emoji", msg: `${label} contains an emoji.` });
	}
	return issues;
}

// ---------------------------------------------------------------------------
// Repetition and wording drift
// ---------------------------------------------------------------------------

const REPEAT_STOP = new Set(
	"a an the of in on at to for and or but is was are be by with as that this it he she his her its their they from not".split(
		" ",
	),
);

const textTokens = (text: string) =>
	(text ?? "")
		.toLowerCase()
		.replace(/[’']/g, "'")
		.replace(/[^a-z0-9' ]+/g, " ")
		.split(/\s+/)
		.filter(Boolean);

/**
 * Phrases of `n` words used more than `max` times in the description body
 * (CONNECT excluded), unless every word is an entity or stop word, or the
 * phrase is the speaker's own wording.
 */
export function repeatedPhrases(
	description: string,
	transcriptText: string,
	entityWords: ReadonlySet<string>,
	n = 4,
	max = 2,
): Array<{ phrase: string; count: number }> {
	const body = description.split("\nCONNECT\n")[0] ?? description;
	const words = textTokens(body);
	const spoken = ` ${textTokens(transcriptText).join(" ")} `;
	const counts = new Map<string, number>();
	for (let i = 0; i + n <= words.length; i++) {
		const k = words.slice(i, i + n).join(" ");
		counts.set(k, (counts.get(k) ?? 0) + 1);
	}
	return [...counts]
		.filter(
			([k, c]) =>
				c > max &&
				!k
					.split(" ")
					.every((w) => entityWords.has(w) || REPEAT_STOP.has(w)) &&
				!spoken.includes(` ${k} `),
		)
		.map(([phrase, count]) => ({ phrase, count }));
}

const DRIFT_STOP = new Set(
	"a an the this that these those he she it they them his her its their him we us our you your i me my is was are were be been being has have had do does did will would can could may might must shall should of in on at to for from by with as and or but if so not no than then there here who whom which what when where why how that's it's there's they're he's she's don't doesn't isn't wasn't weren't can't won't i'm i'd i'll we're you're".split(
		" ",
	),
);
const stem = (w: string) => w.replace(/(ies|es|s|ed|ing|ly)$/, "");
const normW = (w: string) =>
	w
		.toLowerCase()
		.replace(/[’]/g, "'")
		.replace(/[^a-z0-9']/g, "");

export interface TermDrift {
	pos: number;
	outWord: string;
	transcriptWord: string;
	fixable: boolean;
	outPhrase: string;
	transcriptPhrase: string;
}

/**
 * A 7-word window that matches the transcript except for ONE content word is
 * almost always a swapped term ("by reason of" vs the speaker's "by virtue
 * of"). Only interior positions count (at least `margin` matching words on
 * each side): an edge substitution is usually a paraphrase whose window slid
 * onto a different stretch of speech. Pronouns, names, numbers, function
 * words and inflections are never treated as drift.
 */
export function findTermDrift(
	text: string,
	transcriptText: string,
	n = 7,
	margin = 2,
): TermDrift[] {
	const T = transcriptText.split(/\s+/).filter(Boolean);
	const tn = T.map(normW);
	const index = new Map<string, Set<string>>();
	for (let i = 0; i + n <= tn.length; i++) {
		for (let k = 0; k < n; k++) {
			const key = [
				...tn.slice(i, i + k),
				"*",
				...tn.slice(i + k + 1, i + n),
			].join(" ");
			let bucket = index.get(key);
			if (!bucket) {
				bucket = new Set();
				index.set(key, bucket);
			}
			bucket.add(`${tn[i + k]}\u0000${T[i + k]}`);
		}
	}
	const exact = new Set<string>();
	for (let i = 0; i + n <= tn.length; i++)
		exact.add(tn.slice(i, i + n).join(" "));
	const O = text.split(/\s+/).filter(Boolean);
	const on = O.map(normW);
	const found = new Map<string, TermDrift>();
	for (let i = 0; i + n <= on.length; i++) {
		if (exact.has(on.slice(i, i + n).join(" "))) continue;
		for (let k = margin; k < n - margin; k++) {
			const key = [
				...on.slice(i, i + k),
				"*",
				...on.slice(i + k + 1, i + n),
			].join(" ");
			const hits = index.get(key);
			if (!hits) continue;
			for (const h of hits) {
				const [tw = "", traw = ""] = h.split("\u0000");
				const ow = on[i + k] ?? "";
				if (!tw || !ow || tw === ow) continue;
				if (
					DRIFT_STOP.has(tw) ||
					DRIFT_STOP.has(ow) ||
					stem(tw) === stem(ow)
				)
					continue;
				const fixable =
					!/^[A-Z]/.test(O[i + k] ?? "") &&
					!/^[A-Z]/.test(traw) &&
					!/\d/.test(tw + ow);
				const id = `${ow}→${tw}@${i + k}`;
				if (!found.has(id)) {
					found.set(id, {
						pos: i + k,
						outWord: ow,
						transcriptWord: tw,
						fixable,
						outPhrase: O.slice(i, i + n).join(" "),
						transcriptPhrase: key.replace("*", tw),
					});
				}
			}
		}
	}
	return [...found.values()];
}

function applyDrift(
	text: string,
	transcriptText: string,
	log: string[],
	label: string,
): string {
	const drifts = findTermDrift(text, transcriptText).filter((d) => d.fixable);
	if (!drifts.length) return text;
	const parts = text.split(/(\s+)/);
	const positions: number[] = [];
	parts.forEach((w, i) => {
		if (w !== "" && !/^\s+$/.test(w)) positions.push(i);
	});
	for (const d of drifts) {
		const wi = positions[d.pos];
		if (wi === undefined) continue;
		const raw = parts[wi] ?? "";
		parts[wi] = raw.replace(
			new RegExp(escapeRegExp(d.outWord), "i"),
			d.transcriptWord,
		);
		log.push(`${label}: "${d.outWord}" → "${d.transcriptWord}"`);
	}
	return parts.join("");
}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

/** Restore the speaker's word for near-verbatim drifted terms everywhere. */
export function fixTermDrift(
	generated: GeneratedOutput,
	transcriptText: string,
): { generated: GeneratedOutput; fixed: string[] } {
	const log: string[] = [];
	const g = clone(generated);
	g.titleOptions = g.titleOptions.map((t, i) => ({
		...t,
		text: applyDrift(t.text, transcriptText, log, `title ${i + 1}`),
	}));
	g.summaryParagraphs = g.summaryParagraphs.map((s, i) =>
		applyDrift(s, transcriptText, log, `summary ${i + 1}`),
	);
	g.takeaways = g.takeaways.map((t, i) => ({
		...t,
		text: applyDrift(t.text, transcriptText, log, `point ${i + 1}`),
	}));
	g.chapters = g.chapters.map((c, i) => ({
		...c,
		heading: applyDrift(
			c.heading,
			transcriptText,
			log,
			`chapter ${i + 1} heading`,
		),
		summary: applyDrift(
			c.summary,
			transcriptText,
			log,
			`chapter ${i + 1} summary`,
		),
	}));
	g.about = applyDrift(g.about, transcriptText, log, "about");
	return { generated: g, fixed: log };
}

// ---------------------------------------------------------------------------
// The checks
// ---------------------------------------------------------------------------

/** Everything the guards need to know about the run. */
export interface GuardContext {
	videoType: VideoType;
	featuredPeople: VideoPerson[];
	subjectEntity: string | null;
	brandName: string;
	outlet: string | null;
	channel: string | null;
	/** Names the run may use (brand, aliases, saved/confirmed names, glossary). */
	confirmedNames: string[];
	transcriptText: string;
	durationSec: number;
}

const ALWAYS_VOCAB = [
	"I",
	"AI",
	"FOX",
	"LA",
	"PR",
	"B2B",
	"SEO",
	"GA4",
	"URL",
	"ROI",
	"TV",
	"CEO",
	"US",
	"U.S.",
];
const NEVER_PROPER =
	"Former The A An Of In On For And To With From What Why How When Who Can Could Do Does Not Is Are Will Would May Might Should Your You Our This That".split(
		" ",
	);

/** Words that may be capitalized mid-title (names, places, acronyms). */
export function properNounVocab(ctx: GuardContext): Set<string> {
	const v = new Set<string>(ALWAYS_VOCAB);
	const add = (s: string | null | undefined) => {
		for (const w of String(s ?? "").split(/[\s,;:()|/]+/)) {
			const x = w.replace(/['’]s$/, "").replace(/[^A-Za-z0-9-]/g, "");
			if (/^[A-Z]/.test(x)) v.add(x);
		}
	};
	for (const p of ctx.featuredPeople) {
		add(p.name);
		add(p.role);
		add(p.organization);
	}
	for (const s of [
		ctx.brandName,
		ctx.subjectEntity,
		ctx.outlet,
		ctx.channel,
		...ctx.confirmedNames,
	]) {
		add(s);
	}
	// Capitalized mid-sentence words in the transcript are names and places.
	const words = ctx.transcriptText.split(/\s+/);
	words.forEach((w, i) => {
		if (i > 0 && !/[.?!]["”')]*$/.test(words[i - 1] ?? "")) add(w);
	});
	for (const w of NEVER_PROPER) v.delete(w);
	return v;
}

export interface CheckResult {
	ok: boolean;
	bytes: number;
	hard: CheckIssue[];
	soft: CheckIssue[];
}

/**
 * Run every deterministic guard over one candidate (the model output, its
 * derived chapters and the rendered description).
 */
export function runGuards(input: {
	generated: GeneratedOutput;
	chapters: DescriptionChapter[];
	description: string;
	ctx: GuardContext;
}): CheckResult {
	const { generated: g, chapters, description, ctx } = input;
	const hard: CheckIssue[] = [];
	const soft: CheckIssue[] = [];
	const transcriptText = ctx.transcriptText;

	// Titles: 1 to 3 options (over-long ones are dropped, never truncated).
	if (g.titleOptions.length === 0) {
		hard.push({ kind: "title-count", msg: "No valid title option left." });
	} else if (g.titleOptions.length > 3) {
		hard.push({
			kind: "title-count",
			msg: `Expected 3 title options, got ${g.titleOptions.length}.`,
		});
	}
	const strategies = new Set(g.titleOptions.map((t) => t.strategy));
	if (strategies.size !== g.titleOptions.length) {
		hard.push({
			kind: "title-strategy",
			msg: "Each title option must use a different strategy.",
		});
	}
	g.titleOptions.forEach((t, i) => {
		const n = [...t.text].length;
		if (n > YOUTUBE_TITLE_MAX_CHARS) {
			hard.push({
				kind: "title-length",
				fixable: true,
				msg: `Title ${i + 1} is ${n} characters (max ${YOUTUBE_TITLE_MAX_CHARS}).`,
			});
		}
		hard.push(...styleIssues(`Title ${i + 1}`, t.text));
	});

	// Description bytes and style.
	const bytes = utf8ByteLength(description);
	if (bytes > YOUTUBE_DESCRIPTION_MAX_BYTES) {
		hard.push({
			kind: "description-bytes",
			msg: `Description is ${bytes} UTF-8 bytes (max ${YOUTUBE_DESCRIPTION_MAX_BYTES}). Shorten it.`,
		});
	}
	hard.push(...styleIssues("Description", description));
	if (
		/\[[^\]\n]{2,}\]/.test(description) ||
		/ADD (PHONE|EMAIL)/i.test(description)
	) {
		hard.push({
			kind: "placeholder",
			msg: "The description contains a placeholder in square brackets.",
		});
	}

	// Summary shape and the first sentence.
	const first = g.summaryParagraphs[0] ?? "";
	const firstSentence = first.split(/(?<=[.?!])\s/)[0] ?? "";
	if (ctx.videoType === "media-appearance") {
		const person = ctx.featuredPeople[0];
		for (const [what, val] of [
			["the featured person", person?.name],
			["the organization", person?.organization],
			["where it aired", ctx.outlet],
		] as const) {
			if (val && !firstSentence.includes(val)) {
				hard.push({
					kind: "summary-first-sentence",
					msg: `The summary's first sentence must name ${what} ("${val}").`,
				});
			}
		}
		const fold = first.slice(0, YOUTUBE_DESCRIPTION_FOLD_CHARS);
		if (
			person &&
			(!fold.includes(person.name) ||
				(person.organization && !fold.includes(person.organization)))
		) {
			soft.push({
				kind: "above-fold",
				msg: `The first ${YOUTUBE_DESCRIPTION_FOLD_CHARS} characters should name "${person.name}"${person.organization ? ` and "${person.organization}"` : ""}.`,
			});
		}
	}
	if (g.summaryParagraphs.length !== 2) {
		hard.push({
			kind: "summary-paragraphs",
			msg: `Summary must be exactly 2 paragraphs (got ${g.summaryParagraphs.length}).`,
		});
	}
	if (g.takeaways.length < 4 || g.takeaways.length > 6) {
		hard.push({
			kind: "takeaway-count",
			msg: `Need 4 to 6 highlights (got ${g.takeaways.length}).`,
		});
	}

	// Chapters.
	if (chapters.length < YOUTUBE_CHAPTER_MIN_COUNT) {
		hard.push({
			kind: "chapter-count",
			msg: `Need at least ${YOUTUBE_CHAPTER_MIN_COUNT} chapters (got ${chapters.length}).`,
		});
	}
	chapters.forEach((c, i) => {
		if (Number.isNaN(c.sec)) {
			hard.push({
				kind: "chapter-segment",
				fixable: true,
				msg: `Chapter ${i + 1} uses an unknown segment id.`,
			});
		}
		if (i > 0) {
			const prev = chapters[i - 1];
			const gap = c.sec - (prev?.sec ?? 0);
			if (gap <= 0) {
				hard.push({
					kind: "chapter-order",
					fixable: true,
					msg: `Chapter ${i + 1} does not start after chapter ${i}.`,
				});
			} else if (gap < YOUTUBE_CHAPTER_MIN_SEC) {
				hard.push({
					kind: "chapter-short",
					fixable: true,
					msg: `Chapter ${i} is only ${gap}s long (min ${YOUTUBE_CHAPTER_MIN_SEC}s).`,
				});
			}
		}
		if (/^\s*\d{1,2}:\d{2}/.test(c.summary)) {
			hard.push({
				kind: "chapter-summary-timestamp",
				fixable: true,
				msg: `Chapter ${i + 1} summary starts with a timestamp.`,
			});
		}
	});
	const last = chapters[chapters.length - 1];
	if (last && ctx.durationSec - last.sec < YOUTUBE_CHAPTER_MIN_SEC) {
		hard.push({
			kind: "chapter-last",
			fixable: true,
			msg: `The last chapter starts ${Math.round(ctx.durationSec - last.sec)}s before the end (min ${YOUTUBE_CHAPTER_MIN_SEC}s).`,
		});
	}
	if (ctx.durationSec > 0 && chapters.some((c) => c.sec >= ctx.durationSec)) {
		hard.push({
			kind: "chapter-range",
			fixable: true,
			msg: "A chapter starts after the end of the video.",
		});
	}

	// "Originally aired on" only for media appearances.
	if (
		ctx.videoType !== "media-appearance" &&
		/Originally aired on/i.test(description)
	) {
		hard.push({
			kind: "aired-line",
			msg: "Only media appearances may say 'Originally aired on'.",
		});
	}

	// Repetition.
	const entityWords = new Set(
		textTokens(
			[
				ctx.brandName,
				ctx.subjectEntity,
				ctx.outlet,
				...ctx.featuredPeople.flatMap((p) => [
					p.name,
					p.role,
					p.organization,
				]),
				...ctx.confirmedNames,
			]
				.filter(Boolean)
				.join(" "),
		),
	);
	const reps = repeatedPhrases(description, transcriptText, entityWords);
	if (reps.length) {
		hard.push({
			kind: "repetition",
			msg: `These phrases are used more than twice: ${reps.map((r) => `"${r.phrase}" (${r.count}x)`).join(", ")}. Use each at most twice.`,
		});
	}

	// Wording drift.
	for (const dr of findTermDrift(description, transcriptText).filter(
		(x) => x.fixable,
	)) {
		hard.push({
			kind: "term-drift",
			fixable: true,
			msg: `"${dr.outPhrase}" changes the speaker's wording; the speaker said "${dr.transcriptPhrase}".`,
		});
	}

	// Meta-language about our inputs (the word "client" itself is ordinary
	// content for agency videos; only wording about the client's copy is not).
	const published = `${g.titleOptions.map((t) => t.text).join("\n")}\n${description}`;
	const meta = published.match(
		/[^.\n]*\b(according to the client|the client (describes|says|calls)|client copy|the client['’]s (own )?(copy|description|materials))\b[^.\n]*/i,
	);
	if (meta) {
		hard.push({
			kind: "meta-client",
			msg: `Published text refers to the client ("${meta[0].trim()}"). Describe the person, brand or product directly.`,
		});
	}

	// People named who are not confirmed: "<Name> says/explains/…".
	const known = new Set(
		[
			...ctx.featuredPeople.flatMap((p) => {
				const parts = p.name.split(" ");
				return [p.name, parts[parts.length - 1], parts[0]];
			}),
			ctx.brandName,
			ctx.subjectEntity,
			ctx.outlet,
			ctx.channel,
			...ctx.confirmedNames,
		].filter((x): x is string => !!x),
	);
	for (const m of published.matchAll(
		/\b([A-Z][a-z]+(?: [A-Z][a-z]+)?) (says|said|explains|explained|notes|noted|argues|walks|demonstrates|recommends|warns|told|tells|adds|added)\b/g,
	)) {
		const name = m[1] ?? "";
		if (
			!known.has(name) &&
			!/^(The|This|It|He|She|They|Video|Walkthrough|Briefing|Presenter|Host|Anchor|Narrator|Speaker|Panel|Guest|Webinar|Demo)$/.test(
				name.split(" ")[0] ?? "",
			)
		) {
			hard.push({
				kind: "unknown-person",
				msg: `"${m[0]}" attributes a statement to someone who is not a featured or confirmed person; refer to them by role instead.`,
			});
		}
	}

	// Type-specific first sentence.
	if (ctx.videoType === "product-demo") {
		const product = ctx.subjectEntity || ctx.brandName;
		if (
			!firstSentence.includes(product) &&
			!firstSentence.includes(ctx.brandName)
		) {
			hard.push({
				kind: "demo-first-sentence",
				msg: `The first sentence must name the product (${product}) and say what it does.`,
			});
		}
	}
	if (ctx.videoType === "talk" && ctx.featuredPeople[0]) {
		const surname =
			ctx.featuredPeople[0].name.split(" ").slice(-1)[0] ?? "";
		if (!first.includes(surname)) {
			soft.push({
				kind: "talk-presenter",
				msg: `Name the presenter (${ctx.featuredPeople[0].name}) in the first paragraph.`,
			});
		}
	}

	// Titles: names, credentials, sentence case, drift.
	const vocab = properNounVocab(ctx);
	const formerPhrases = ctx.featuredPeople
		.flatMap((p) =>
			[
				...String(p.role ?? "").matchAll(
					/former\s+((?:[A-Z][\w.]*\s?)+)/gi,
				),
			].flatMap((m) => {
				const w = (m[1] ?? "").trim().split(/\s+/);
				return w.slice(0, -1).map((_, k) => w.slice(k).join(" "));
			}),
		)
		.sort((a, b) => b.length - a.length);
	g.titleOptions.forEach((t, i) => {
		const s = t.text;
		const lead = ctx.featuredPeople[0];
		if (
			ctx.videoType === "media-appearance" &&
			lead &&
			!s.includes(lead.name)
		) {
			hard.push({
				kind: "title-name",
				msg: `Title ${i + 1} must include the featured person's full name "${lead.name}".`,
			});
		}
		if (/\b(AG|DAG|DA|DOJ|Atty|Esq)\b/.test(s)) {
			hard.push({
				kind: "title-abbrev",
				msg: `Title ${i + 1} abbreviates a credential or office; spell it out.`,
			});
		}
		for (const p of ctx.featuredPeople) {
			const surname = p.name.split(" ").slice(-1)[0] ?? "";
			if (
				p.name.includes(" ") &&
				surname &&
				new RegExp(`\\b${escapeRegExp(surname)}\\b`).test(s) &&
				!s.includes(p.name)
			) {
				hard.push({
					kind: "title-surname",
					msg: `Title ${i + 1} uses "${surname}" alone; use the full name "${p.name}".`,
				});
			}
		}
		for (const ph of formerPhrases) {
			const at = s.toLowerCase().indexOf(ph.toLowerCase());
			if (at < 0) continue;
			if (
				!/former\s+(\S+\s+){0,1}$/i.test(
					s.slice(Math.max(0, at - 25), at),
				)
			) {
				hard.push({
					kind: "credential-former",
					msg: `Title ${i + 1} gives "${ph}" without "former".`,
				});
			}
			break;
		}
		const caps: string[] = [];
		for (const seg of s.split(/\s*[?!:|(]\s*|\s+[–-]\s+/).filter(Boolean)) {
			for (const w of seg.split(/\s+/).slice(1)) {
				const x = w.replace(/['’]s$/, "").replace(/[^A-Za-z0-9-]/g, "");
				if (/^[A-Z][a-z]/.test(x) && !vocab.has(x)) caps.push(x);
			}
		}
		if (caps.length >= 1) {
			hard.push({
				kind: "title-case",
				msg: `Title ${i + 1} is in Title Case (${caps.join(", ")}); use sentence case.`,
			});
		}
		for (const dr of findTermDrift(s, transcriptText).filter(
			(x) => x.fixable,
		)) {
			hard.push({
				kind: "term-drift",
				fixable: true,
				msg: `Title ${i + 1}: "${dr.outPhrase}" changes the speaker's wording ("${dr.transcriptPhrase}").`,
			});
		}
	});

	return { ok: hard.length === 0, bytes, hard, soft };
}

// ---------------------------------------------------------------------------
// Deterministic fixes (after the one repair pass)
// ---------------------------------------------------------------------------

const fixText = (s: string) =>
	s
		.replace(/\s*—\s*/gu, ", ")
		.replace(/[<>]/g, "")
		.replace(/\s+,/g, ",")
		.trim();

/**
 * Remove em dashes and angle brackets, drop over-long titles (never
 * truncate), strip timestamps from chapter summaries, and drop chapters with
 * unknown segments, out of order, too close together or too near the end.
 */
export function deterministicFix(
	generated: GeneratedOutput,
	segments: readonly Pick<TranscriptSegment, "id" | "startSec">[],
	durationSec: number,
): { generated: GeneratedOutput; applied: string[] } {
	const applied: string[] = [];
	const g = clone(generated);
	const touch = (label: string, before: string, after: string) => {
		if (before !== after) applied.push(label);
		return after;
	};
	g.titleOptions = g.titleOptions.map((t, i) => ({
		...t,
		text: touch(`title ${i + 1}: dashes/brackets`, t.text, fixText(t.text)),
	}));
	const keptTitles = g.titleOptions.filter(
		(t) => [...t.text].length <= YOUTUBE_TITLE_MAX_CHARS,
	);
	if (keptTitles.length < g.titleOptions.length) {
		for (const t of g.titleOptions) {
			if ([...t.text].length > YOUTUBE_TITLE_MAX_CHARS)
				applied.push(`dropped over-long title: "${t.text}"`);
		}
		g.titleOptions = keptTitles;
	}
	g.summaryParagraphs = g.summaryParagraphs.map((s, i) =>
		touch(`summary ${i + 1}`, s, fixText(s)),
	);
	g.takeaways = g.takeaways.map((t, i) => ({
		...t,
		text: touch(`point ${i + 1}`, t.text, fixText(t.text)),
	}));
	g.about = touch("about", g.about, fixText(g.about));
	g.chapters = g.chapters.map((c, i) => ({
		...c,
		heading: touch(
			`chapter ${i + 1} heading`,
			c.heading,
			fixText(c.heading),
		),
		summary: touch(
			`chapter ${i + 1} summary`,
			c.summary,
			fixText(c.summary).replace(/^\s*\d{1,2}:\d{2}(:\d{2})?\s*/, ""),
		),
	}));

	const byId = new Map(segments.map((s) => [s.id, s]));
	const before = g.chapters.length;
	const known = g.chapters
		.filter((c) => byId.has(parseSegmentRef(c.segmentId)))
		.sort(
			(a, b) =>
				(byId.get(parseSegmentRef(a.segmentId))?.startSec ?? 0) -
				(byId.get(parseSegmentRef(b.segmentId))?.startSec ?? 0),
		);
	const kept: GeneratedOutput["chapters"] = [];
	let prevT: number | null = null;
	for (const c of known) {
		const t =
			kept.length === 0
				? 0
				: Math.floor(
						byId.get(parseSegmentRef(c.segmentId))?.startSec ?? 0,
					);
		if (prevT !== null && t - prevT < YOUTUBE_CHAPTER_MIN_SEC) continue;
		if (kept.length && durationSec - t < YOUTUBE_CHAPTER_MIN_SEC) continue;
		kept.push(c);
		prevT = t;
	}
	if (kept.length !== before)
		applied.push(`chapters: dropped ${before - kept.length}`);
	g.chapters = kept;
	return { generated: g, applied };
}

/**
 * Drop the highlight most redundant with the summary until the description
 * fits (never below 4), then fold the shortest chapter into the one before
 * it (never below 5).
 */
export function trimToBytes(
	generated: GeneratedOutput,
	render: (g: GeneratedOutput) => string,
	segmentStart: (segmentRefValue: string) => number,
): { generated: GeneratedOutput; dropped: string[] } {
	const g = clone(generated);
	const dropped: string[] = [];
	const tok = (s: string) =>
		new Set(
			s
				.toLowerCase()
				.replace(/[^a-z0-9 ]/g, " ")
				.split(/\s+/)
				.filter((w) => w.length > 3),
		);
	const summary = tok(g.summaryParagraphs.join(" "));
	while (
		utf8ByteLength(render(g)) > YOUTUBE_DESCRIPTION_MAX_BYTES &&
		g.takeaways.length > 4
	) {
		let worst = 0;
		let worstScore = -1;
		g.takeaways.forEach((t, i) => {
			const w = tok(t.text);
			const score =
				[...w].filter((x) => summary.has(x)).length /
				Math.max(1, w.size);
			if (score > worstScore) {
				worstScore = score;
				worst = i;
			}
		});
		dropped.push(`dropped highlight "${g.takeaways[worst]?.text ?? ""}"`);
		g.takeaways.splice(worst, 1);
	}
	while (
		utf8ByteLength(render(g)) > YOUTUBE_DESCRIPTION_MAX_BYTES &&
		g.chapters.length > 5
	) {
		const times = g.chapters.map((c) => segmentStart(c.segmentId));
		let shortest = 1;
		let best = Number.POSITIVE_INFINITY;
		for (let i = 1; i < g.chapters.length; i++) {
			const len =
				(i + 1 < times.length
					? (times[i + 1] ?? 0)
					: Number.POSITIVE_INFINITY) - (times[i] ?? 0);
			if (len < best) {
				best = len;
				shortest = i;
			}
		}
		dropped.push(`folded chapter "${g.chapters[shortest]?.heading ?? ""}"`);
		g.chapters.splice(shortest, 1);
	}
	return { generated: g, dropped };
}
